import type {
  TriviaConfig,
  TriviaPlaythrough,
  TriviaQuestionModel,
} from "./trivia-types";

// StrictMode double-invokes memoized computations, so a Math.random()-based shuffle would
// reorder differently each time createPlaythrough happens to re-run. A seeded PRNG makes the
// deck a pure function of the seed, so re-running it (StrictMode, memo eviction) reproduces
// the identical deck.
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;

  return function next() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const result = items.slice();

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

export function createPlaythrough(
  config: TriviaConfig,
  seed: number,
): TriviaPlaythrough {
  const random = mulberry32(seed);

  let questions: TriviaQuestionModel[] = config.randomizeQuestions
    ? shuffle(config.questions, random)
    : config.questions;

  if (config.randomizeAnswers) {
    questions = questions.map((question) => ({
      ...question,
      answers: shuffle(question.answers, random),
    }));
  }

  return { seed, questions };
}
